/** A number field that applies its value when you LEAVE it, not on every key.
 *
 * Every number edited in the basket reprices the whole basket on the server.
 * Committing per keystroke meant typing 320 priced 3, then 32, then 320 —
 * three requests, three flashes, and a table that moved under the cursor
 * (operator, 2026-11-04: *"as soon as i put 3 its refreshing and then i put 2
 * its refreshing"*).
 *
 * What commits, and what does not:
 *   typing        waits — the digits stay in the field until blur or Enter
 *   Enter / blur  commits the typed value
 *   Escape        drops the typed value and shows the stored one again
 *   spinner, ↑ ↓  commit AT ONCE — a click on a stepper is one deliberate
 *                 nudge, and waiting for a blur would make it feel dead
 *
 * The browser tells the two apart by the event's `inputType`: typing reports
 * an "insert..." or "delete..." type, while the spinner and the arrow keys
 * report `insertReplacementText` or nothing at all.
 */

import { useState, type ReactElement } from "react";

interface Props {
  /** null shows an empty field: a leg the archive cannot price can still be typed into. */
  value: number | null;
  onCommit: (next: number) => void;
  label: string;
  className: string;
  min?: number;
  max?: number;
  step?: number;
}

/** True for a keystroke edit (typing, delete, paste, cut), false for a
 * spinner click or an arrow key. */
function isTyping(event: Event): boolean {
  const kind = (event as InputEvent).inputType;
  return typeof kind === "string" && /^(insert(?!ReplacementText)|delete)/.test(kind);
}

export function CommitNumberInput({
  value,
  onCommit,
  label,
  className,
  min,
  max,
  step,
}: Readonly<Props>): ReactElement {
  // null = not being edited: the field shows the stored value.
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (text: string | null): void => {
    setDraft(null);
    if (text === null || text.trim() === "") return;
    const next = Number(text);
    if (Number.isFinite(next) && next !== value) onCommit(next);
  };

  return (
    <input
      type="number"
      aria-label={label}
      className={className}
      min={min}
      max={max}
      step={step}
      value={draft ?? (value === null ? "" : String(value))}
      onChange={(e) => {
        if (isTyping(e.nativeEvent)) setDraft(e.target.value);
        else commit(e.target.value);
      }}
      onBlur={() => {
        commit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit(draft);
        if (e.key === "Escape") setDraft(null);
      }}
    />
  );
}
